import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useFormAnalytics } from '../../hooks/useFormAnalytics';
import { parseLinearScaleOptions } from '../../adapters/field-type.adapter';
import { FieldAnalyticsCard } from './FieldAnalyticsCard';
import { FieldResponsesDialog } from './FieldResponsesDialog';
import type { FieldResponsesTarget } from './FieldResponsesDialog';
import type { FormField } from '../../types';

interface FormAnalyticsDashboardProps {
  formId: string;
  formCreatedAt: string;
  /**
   * The analytics DTO has no `options`, so a `LinearScale` field's real
   * min/max/labels (needed for the scale-bar visualization) aren't in it —
   * looked up here from the already-loaded form detail instead of an extra fetch.
   */
  formFields: FormField[];
}

export function FormAnalyticsDashboard({
  formId,
  formCreatedAt,
  formFields,
}: FormAnalyticsDashboardProps) {
  const { t, i18n } = useTranslation('forms');
  const { analytics, isLoading } = useFormAnalytics(formId);
  const [drillDown, setDrillDown] = useState<FieldResponsesTarget | null>(null);

  if (isLoading || !analytics) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3" aria-hidden="true">
        <Skeleton className="h-20 w-full rounded" />
        <Skeleton className="h-20 w-full rounded" />
        <Skeleton className="h-20 w-full rounded" />
      </div>
    );
  }

  const sinceDate = new Date(formCreatedAt).toLocaleDateString(i18n.language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className="space-y-4" data-testid="forms-analytics-dashboard">
      <div className="flex flex-col divide-y divide-border border-b pb-4 sm:flex-row sm:divide-x sm:divide-y-0">
        <div className="py-3 first:pt-0 sm:px-6 sm:py-0 sm:first:pl-0">
          <p className="text-xs text-muted-foreground mb-1">{t('submissions.totalResponses')}</p>
          <p className="text-2xl font-bold text-primary">
            {analytics.totalSubmissions}
            <span className="ml-1 text-xs font-normal text-muted-foreground">
              · {t('submissions.analytics.since', { date: sinceDate })}
            </span>
          </p>
        </div>

        {analytics.responseRate !== null && (
          <div className="py-3 first:pt-0 sm:px-6 sm:py-0 sm:first:pl-0">
            <p className="text-xs text-muted-foreground mb-1">
              {t('submissions.analytics.completionRate')}
            </p>
            <p className="text-2xl font-bold text-green-600">
              {analytics.responseRate}%
              <span className="ml-1 text-xs font-normal text-muted-foreground">
                {t('submissions.analytics.respondedOfTotal', {
                  responded: analytics.respondedRecipients,
                  total: analytics.totalRecipients,
                })}
              </span>
            </p>
            <div className="h-1.5 w-full max-w-40 rounded-full bg-muted mt-2">
              <div
                className="h-1.5 rounded-full bg-green-600"
                style={{ width: `${Math.min(analytics.responseRate, 100)}%` }}
              />
            </div>
          </div>
        )}

        {analytics.highestAbandonmentField && (
          <div className="py-3 first:pt-0 sm:px-6 sm:py-0 sm:first:pl-0">
            <p className="text-xs text-muted-foreground mb-1">
              {t('submissions.analytics.mostAbandoned')}
            </p>
            <p className="flex flex-wrap items-center gap-1 text-sm">
              <AlertCircle className="h-4 w-4 shrink-0 text-orange-600" />
              <span className="font-semibold text-orange-600">
                {analytics.highestAbandonmentField.label}
              </span>
              <span className="text-xs text-muted-foreground">
                ·{' '}
                {t('submissions.analytics.stoppedThere', {
                  count: analytics.highestAbandonmentField.emptyCount,
                })}
              </span>
            </p>
          </div>
        )}
      </div>

      {analytics.fields.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm font-semibold">{t('submissions.analytics.breakdownTitle')}</p>
          {analytics.fields.map((field) => {
            const matchingField = formFields.find((f) => f.id === field.fieldId);
            const scaleOptions =
              field.fieldType === 'LinearScale'
                ? parseLinearScaleOptions(matchingField?.options ?? null)
                : undefined;
            return (
              <FieldAnalyticsCard
                key={field.fieldId}
                field={field}
                scaleOptions={scaleOptions}
                onDrillDown={setDrillDown}
              />
            );
          })}
        </div>
      )}

      <FieldResponsesDialog
        formId={formId}
        target={drillDown}
        onOpenChange={(open) => !open && setDrillDown(null)}
      />
    </div>
  );
}
